import * as React from 'react';
import { css } from '@emotion/core';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { FORM_COLUMN } from '../../forms/styles/form';
import { ERROR_MSG } from '../../forms/styles/messages';
import LocalInput from '../../localization/LocalInput';
import { INPUT } from '../../forms/styles/input';
import LocalBtn from '../../localization/LocalBtn';
import { btnStyle } from '../../forms/styles/button';
import { useFormik } from 'formik';
import { newPasswordFormValidation } from '../formValidations';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import { COLORS } from '../../globalStyle/colors';


type Props = {
  className?: string;
  minPwLength?: number;
  requireSymbol?: boolean;
  serverSideValidationError: boolean;
  isLoading: boolean;
  createPassword: (password: string) => void;
  pwRequirements?: string;
};


/**
 * NewPasswordForm: a component that provides the user with a form in which to enter:
 *
 * * a new password
 * * a re-iteration of the first form's value, confirming their new password
 *
 * The form will only send the reset request once both input values match and the supplied value satisfies the portal's
 * password strength requirements.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
const NewPasswordForm: React.FC<Props> = props => {
  const pwReqsNotMet  = useMsgText(Str.PasswordFailsRequirements);
  const pwsDoNotMatch = useMsgText(Str.PasswordMismatch);

  const { handleSubmit, handleChange, isValid, errors, values } = useFormik({
    initialValues:    {
      password:        '',
      confirmPassword: '',
    },
    onSubmit:         (values) => {
      props.createPassword(values.password);
    },
    validationSchema: newPasswordFormValidation(pwReqsNotMet, pwsDoNotMatch, props.minPwLength, props.requireSymbol),
    validateOnChange: false,
  });

  return (
    <div
      className={props.className}
    >
      <div
        css={
          css`margin-bottom: 12px;`
        }
      >
        {props.pwRequirements}
      </div>
      <fieldset
        disabled={props.isLoading}
        css={[
          css`
            border: 0 none; // Chrome adds padding and margins to fieldsets by default
            margin: 0;
            padding: 0;
          `,
        ]}
      >
        <form
          onSubmit={handleSubmit}
          css={[
            FLEX_COLUMN,
            FORM_COLUMN,
          ]}
        >
          {
            (props.serverSideValidationError || !isValid) && (
              <div
                css={[
                  FLEX_ROW,
                  ERROR_MSG,
                  css`
                  align-items: center;
                `,
                ]}
              >
                {
                  props.serverSideValidationError
                    ? `${pwReqsNotMet}`
                    : `${errors.password ?? errors.confirmPassword}`
                }
              </div>
            )
          }
          <LocalInput
            placeholderTranslation={Str.NewPassword}
            name='password'
            type='password'
            value={values.password}
            css={[
              INPUT(),
            ]}
            onChange={handleChange}
            autoComplete='off'
          />
          <LocalInput
            placeholderTranslation={Str.ConfirmPassword}
            name='confirmPassword'
            type='password'
            value={values.confirmPassword}
            css={[
              INPUT(),
            ]}
            onChange={handleChange}
            autoComplete='off'
          />
          <LocalBtn
            str={Str.Continue}
            type='submit'
            css={btnStyle.primary(COLORS.GREEN)}
          />
        </form>
      </fieldset>
    </div>
  );
};


export default NewPasswordForm;
